import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import React, { useState } from "react";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import { displayEmail } from "../../utils";

const STATUS_COLORS = {
  paid: { bg: "#E4F5EA", color: TEXT.success },
  partially_paid: { bg: "#FDF2CF", color: TEXT.warning },
  sent: { bg: "#E6F0FD", color: TEXT.info },
  draft: { bg: "#F2F4F7", color: TEXT.secondary },
  cancelled: { bg: "#FDE7E7", color: TEXT.error },
  pending: { bg: "#F2F4F7", color: TEXT.secondary },
  failed: { bg: "#FDE7E7", color: TEXT.error },
  initiated: { bg: "#FDF2CF", color: TEXT.warning },
};

const useStyles = makeStyles((theme) => ({
  tableHeadRow: {
    backgroundColor: "#f5f5f5",
    "& th": {
      padding: "12px 10px",
      fontWeight: 600,
      fontSize: "0.875rem",
    },
  },
  tableBodyCell: {
    padding: "12px 10px",
    fontSize: "0.875rem",
  },
  clickableRow: {
    cursor: "pointer",
  },
  errorText: {
    color: theme.palette.error.main,
    padding: "1rem",
  },
}));

function StatusChip({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <Chip label={(status || "").replace("_", " ")} size="small"
          style={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, textTransform: "capitalize" }} />
  );
}

function ChallanRow({ c, serialNo, classes }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow hover className={classes.clickableRow} onClick={() => setOpen((o) => !o)}>
        <TableCell className={classes.tableBodyCell}>{serialNo}</TableCell>
        <TableCell className={classes.tableBodyCell}>{c.challan_number}</TableCell>
        <TableCell className={classes.tableBodyCell}>{c.case_number}</TableCell>
        <TableCell className={classes.tableBodyCell}>₹{c.total_fee}</TableCell>
        <TableCell className={classes.tableBodyCell}>{c.party_shares.length}</TableCell>
        <TableCell className={classes.tableBodyCell}><StatusChip status={c.status} /></TableCell>
        <TableCell className={classes.tableBodyCell}>{c.created_at}</TableCell>
        <TableCell className={classes.tableBodyCell} align="center">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} style={{ paddingTop: 0, paddingBottom: 0, borderBottom: "1px solid #eee" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={2}>
              <Box p={1.5}>
                <Typography style={{ fontWeight: 600 }}>
                  Party shares
                </Typography>
              </Box>
              <Paper style={{ boxShadow: "none", border: "1px solid #ddd", borderBottom: "none" }}>
                <Table size="small" style={{maxWidth: "100%", margin: 0}}>
                  <TableHead>
                    <TableRow className={classes.tableHeadRow}>
                      <TableCell>Party</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Mode</TableCell>
                      <TableCell>Paid At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {c.party_shares.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className={classes.tableBodyCell}>
                          {s.party_name}
                          <div style={{ fontSize: "0.7rem", color: TEXT.muted }}>{displayEmail(s.party_email)}</div>
                        </TableCell>
                        <TableCell className={classes.tableBodyCell}>₹{s.amount}</TableCell>
                        <TableCell className={classes.tableBodyCell}><StatusChip status={s.payment_status} /></TableCell>
                        <TableCell className={classes.tableBodyCell}>{s.payment_mode || "—"}</TableCell>
                        <TableCell className={classes.tableBodyCell}>{s.paid_at || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function PaymentChallansTab({
  challans,
  loading,
  error,
  page,
  rowsPerPage,
  totalCount,
  onChangePage,
}) {
  const classes = useStyles();

  return (
    <>
      {error && (
        <Typography className={classes.errorText} variant="body2">
          {error}
        </Typography>
      )}
      {loading ? (
        <Box p={4}>
          <CircularCenteredLoader />
        </Box>
      ) : (
      <Paper style={{ width: "100%", overflowX: "auto", boxShadow: "none", border: "1px solid #ddd" }}>
        <TableContainer>
          <Table size="small" style={{ maxWidth: "100%", marginTop: 0 }}>
            <TableHead>
              <TableRow className={classes.tableHeadRow}>
                <TableCell>S.No.</TableCell>
                <TableCell>Challan No.</TableCell>
                <TableCell>Case ID</TableCell>
                <TableCell>Total Fee</TableCell>
                <TableCell>Parties</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center" />
              </TableRow>
            </TableHead>
            <TableBody>
              {challans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: "2rem" }}>
                    <Typography color="textSecondary">
                      No payment challans yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                challans.map((c, idx) => (
                  <ChallanRow
                    key={c.id}
                    c={c}
                    serialNo={totalCount - (page * rowsPerPage + idx)}
                    classes={classes}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={onChangePage}
        />
      </Paper>
      )}
    </>
  );
}
